import { createStyles } from "@mantine/core";

// Note Settings page — master/detail layout mirroring the redesigned Notes
// module settings surface. Theme-aware via the app --app-* design tokens.
const useSettingsStyles = createStyles(() => ({
  page: {
    width: "100%",
    minHeight: "100dvh",
    backgroundColor: "var(--app-bg)",
    fontFamily: "Inter, sans-serif",
  },
  backlink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text-muted)",
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1,
    cursor: "pointer",
    borderRadius: 8,
    padding: "7px 11px",
    margin: "14px 20px 4px",
    "&:hover": {
      background: "var(--app-surface-2)",
      color: "var(--app-text)",
    },
  },
  sectitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "var(--app-text)",
    margin: "8px 20px 14px",
  },

  // ---- master / detail ----
  setwrap: {
    display: "grid",
    gridTemplateColumns: "240px 1fr",
    gap: 18,
    padding: "0 20px 40px",
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr",
    },
  },
  setnav: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    padding: 8,
    boxShadow: "0 1px 2px rgba(16, 24, 40, .05)",
    alignSelf: "start",
    position: "sticky",
    top: 90,
    "@media (max-width: 900px)": {
      position: "static",
    },
  },
  navGroup: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".06em",
    color: "var(--app-text-faint, #8A93A2)",
    padding: "12px 10px 6px",
  },
  navBtn: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 10,
    border: "none",
    background: "none",
    font: "inherit",
    fontSize: 13.5,
    color: "var(--app-text-muted)",
    padding: "9px 10px",
    borderRadius: 8,
    cursor: "pointer",
    textAlign: "left",
    fontWeight: 500,
    "&:hover": {
      background: "var(--app-surface-2)",
      color: "var(--app-text)",
    },
  },
  navBtnActive: {
    background: "var(--app-primary-soft, #FFF6E9)",
    color: "#8A5B12",
    fontWeight: 600,
    "&:hover": {
      background: "var(--app-primary-soft, #FFF6E9)",
      color: "#8A5B12",
    },
  },

  // ---- setting card ----
  card: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16, 24, 40, .05)",
    alignSelf: "start",
    marginBottom: 16,
  },
  cardHead: {
    padding: "18px 20px",
    borderBottom: "1px solid var(--app-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  cardHeadTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--app-text)",
  },
  cardHeadSub: {
    fontSize: 12.5,
    color: "var(--app-text-muted)",
    marginTop: 3,
  },
  cardBody: {
    padding: "8px 20px 18px",
  },

  // ---- rows ----
  srow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    padding: "15px 0",
    borderBottom: "1px solid var(--app-border)",
    "&:last-child": { borderBottom: "none" },
  },
  srowLab: {
    fontSize: 13.5,
    fontWeight: 600,
    color: "var(--app-text)",
  },
  srowDsc: {
    fontSize: 12.5,
    color: "var(--app-text-muted)",
    marginTop: 2,
    maxWidth: 440,
  },

  // ---- templates ----
  tplPanel: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16, 24, 40, .05)",
    overflow: "hidden",
  },
  tplPanelHead: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    padding: "16px 20px",
    borderBottom: "1px solid var(--app-border)",
  },
  tplPanelBody: {
    padding: 20,
  },
  tplHead: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  tplGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
    "@media (max-width: 1100px)": { gridTemplateColumns: "repeat(2, 1fr)" },
    "@media (max-width: 700px)": { gridTemplateColumns: "1fr" },
  },
  tplNew: {
    border: "1.5px dashed var(--app-border)",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "var(--app-text-muted)",
    gap: 8,
    minHeight: 200,
    cursor: "pointer",
    background: "var(--app-surface)",
    transition: ".12s",
    "&:hover": {
      borderColor: "var(--app-primary)",
      color: "#B4700A",
      background: "var(--app-primary-soft, #FFF6E9)",
    },
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "40vh",
  },

  // ---- incident providers ----
  contactRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 8,
    background: "var(--app-surface)",
  },
  contactAv: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "var(--app-surface-2)",
    color: "var(--app-text-muted)",
    display: "grid",
    placeItems: "center",
    fontSize: 11,
    fontWeight: 600,
    flex: "none",
  },

  // ---- misc ----
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 20,
    padding: "3px 12px",
    background: "var(--app-info-bg, #EFF8FF)",
    color: "var(--app-info-fg, #175CD3)",
    border: "1px solid var(--app-info-bd, #B2DDFF)",
  },
  btnPrimary: {
    backgroundColor: "var(--app-primary) !important",
    border: "1px solid var(--app-primary)",
    color: "var(--app-on-primary) !important",
    fontWeight: 600,
    borderRadius: 8,
    "&:hover": { filter: "brightness(0.96)" },
  },
  btnGhost: {
    backgroundColor: "var(--app-surface) !important",
    border: "1px solid var(--app-border)",
    color: "var(--app-text) !important",
    fontWeight: 600,
    borderRadius: 8,
    "&:hover": { backgroundColor: "var(--app-surface-2) !important" },
  },
  spacer: { flex: 1 },
}));

export default useSettingsStyles;
